import React from "react";
import { motion } from "framer-motion";
import { Crown, Star, Shield, Gem, Flame, ChevronRight } from "lucide-react";
import { useSelector } from "react-redux";
import { UserModelData } from "../../types";
import useThemeHook from "../hooks/useThemeHook";
import { useNavigate } from "react-router-dom";
import { PAGE_NAME } from "../../types";

interface VIPTier {
  name: string;
  minDeposit: number;
  icon: React.ReactNode;
  color: string;
  bgClass: string;
  borderClass: string;
  boostPercent: number;
  perks: string[];
}

const VIP_TIERS: VIPTier[] = [
  {
    name: "Bronze",
    minDeposit: 10,
    icon: <Shield size={18} className="text-amber-700" />,
    color: "text-amber-700",
    bgClass: "bg-amber-100",
    borderClass: "border-amber-300",
    boostPercent: 10,
    perks: ["+10% mining boost", "Bronze badge"],
  },
  {
    name: "Silver",
    minDeposit: 50,
    icon: <Star size={18} className="text-slate-500" />,
    color: "text-slate-500",
    bgClass: "bg-slate-100",
    borderClass: "border-slate-300",
    boostPercent: 25,
    perks: ["+25% mining boost", "1 free spin/day"],
  },
  {
    name: "Gold",
    minDeposit: 200,
    icon: <Crown size={18} className="text-yellow-500" />,
    color: "text-yellow-500",
    bgClass: "bg-yellow-50",
    borderClass: "border-yellow-300",
    boostPercent: 50,
    perks: ["+50% mining boost", "2 free spins/day"],
  },
  {
    name: "Diamond",
    minDeposit: 500,
    icon: <Gem size={18} className="text-cyan-500" />,
    color: "text-cyan-500",
    bgClass: "bg-cyan-50",
    borderClass: "border-cyan-300",
    boostPercent: 100,
    perks: ["+100% mining boost", "3 free spins/day"],
  },
  {
    name: "Legend",
    minDeposit: 1000,
    icon: <Flame size={18} className="text-red-500" />,
    color: "text-red-500",
    bgClass: "bg-red-50",
    borderClass: "border-red-300",
    boostPercent: 200,
    perks: ["+200% mining boost", "Unlimited spins"],
  },
];

const VIPTierBadge: React.FC = () => {
  const theme = useThemeHook();
  const navigate = useNavigate();
  const userData: UserModelData = useSelector((state: any) => state?.actualUser);
  const user = userData?.user;
  const totalDeposited = user?.totalPiDeposited || 0;

  // Find current tier
  let currentTierIndex = -1;
  for (let i = VIP_TIERS.length - 1; i >= 0; i--) {
    if (totalDeposited >= VIP_TIERS[i].minDeposit) {
      currentTierIndex = i;
      break;
    }
  }

  const currentTier = currentTierIndex >= 0 ? VIP_TIERS[currentTierIndex] : null;
  const nextTier = currentTierIndex < VIP_TIERS.length - 1 ? VIP_TIERS[currentTierIndex + 1] : null;

  // Progress to next tier
  const currentMin = currentTier ? currentTier.minDeposit : 0;
  const nextMin = nextTier ? nextTier.minDeposit : currentMin;
  const progress = nextTier
    ? Math.min(((totalDeposited - currentMin) / (nextMin - currentMin)) * 100, 100)
    : 100;
  const remaining = nextTier ? nextMin - totalDeposited : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`p-4 rounded-2xl border ${
        theme === "dark"
          ? "bg-slate-800/60 border-slate-700"
          : currentTier
          ? `${currentTier.bgClass} ${currentTier.borderClass}`
          : "bg-gray-50 border-gray-200"
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {currentTier ? (
            <>
              {currentTier.icon}
              <span
                className={`font-black text-sm ${
                  theme === "dark" ? "text-white" : currentTier.color
                }`}
              >
                VIP {currentTier.name}
              </span>
            </>
          ) : (
            <>
              <Shield size={18} className="text-gray-400" />
              <span
                className={`font-black text-sm ${
                  theme === "dark" ? "text-slate-400" : "text-gray-500"
                }`}
              >
                No VIP Tier
              </span>
            </>
          )}
        </div>
        <button
          onClick={() => navigate(PAGE_NAME.DEPOSIT)}
          className="flex items-center gap-1 px-3 py-1 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-bold active:scale-95 transition-all"
        >
          Deposit Pi <ChevronRight size={12} />
        </button>
      </div>

      {/* Progress bar */}
      {nextTier && (
        <>
          <div
            className={`h-2 rounded-full overflow-hidden mb-2 ${
              theme === "dark" ? "bg-slate-700" : "bg-white/60"
            }`}
          >
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full"
            />
          </div>
          <p
            className={`text-[10px] font-bold ${
              theme === "dark" ? "text-slate-400" : "text-gray-600"
            }`}
          >
            Deposit {remaining.toFixed(1)} more π to reach{" "}
            <span className={nextTier.color}>{nextTier.name}</span> →{" "}
            {nextTier.perks[0]}
          </p>
        </>
      )}

      {/* Current perks */}
      {currentTier && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {currentTier.perks.map((perk, i) => (
            <span
              key={i}
              className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${
                theme === "dark"
                  ? "bg-slate-700 text-slate-300"
                  : "bg-white/80 text-gray-600"
              }`}
            >
              {perk}
            </span>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default VIPTierBadge;
